import React, { Component } from 'react';
import { MobileTruckLoadContext } from './MobileTruckLoadContext';

// Backs up the form's own fields (not the live scan session - expectedHUs etc.
// stay real-data-only) so a refresh doesn't force the operator to redo their plan
// selection or retype vehicle/LSP/dock/scanner. sessionStorage (not localStorage) -
// this is scoped to "this device, right now", not meant to survive a closed tab
// showing up as a stale draft days later. Cleared by resetSessionState (explicit
// fresh start via the header menu, or a port switch), same signal that already
// wipes the live session.
const DRAFT_STORAGE_KEY = 'mtl_input_plan_draft';
const DRAFT_FIELDS = ['loadDate', 'planId', 'loadType', 'loadVehicle', 'vehicleNumber', 'lsp', 'dockNumber', 'scannerName'];

function loadInputPlanDraft() {
    try {
        return JSON.parse(sessionStorage.getItem(DRAFT_STORAGE_KEY)) || null;
    } catch (e) {
        return null;
    }
}

function saveInputPlanDraft(fields) {
    try {
        sessionStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(fields));
    } catch (e) { /* ignore - draft persistence is a convenience, not required */ }
}

// Ported from test.html's screen1 (Input Plan / Start Loading form), now backed by
// real uploaded plan data instead of the mock RAW_PLAN_ROWS - see
// MobileTruckLoadContext.js's "Real, upload-driven data" section. One fetch loads
// every plan row already uploaded for this warehouse + Load Date (via each row's
// own plan_date/"Plan Date" field); Plan Id/Load Type/Trip No are all derived from
// that single array client-side as the user narrows the selection - no per-step
// network round-trip. Trip No is disabled once its lock flips (Start Loading
// already tapped for it, by anyone).
export default class MobileInputPlan extends Component {
    static contextType = MobileTruckLoadContext;

    constructor(props, context) {
        super(props, context);
        var session = context.session;
        // The live session (context) only has anything in it if this mount didn't
        // follow a refresh (e.g. came back from Scan). If it's empty, fall back to
        // the sessionStorage draft - the only other place these fields could still
        // be sitting, since a refresh wipes context but not sessionStorage 
        var draft = (!session.planId && !session.loadVehicle) ? (loadInputPlanDraft() || {}) : {};
        this.state = {
            loadDate: session.loadDate || draft.loadDate || new Date().toISOString().slice(0, 10),
            // Restore whatever was last entered (e.g. coming back from Scan
            // mid-session, or a refresh) instead of wiping the manual-input fields
            // blank again.
            planId: session.planId || draft.planId || '',
            loadType: session.loadType || draft.loadType || '',
            loadVehicle: session.loadVehicle || draft.loadVehicle || '',
            vehicleNumber: session.vehicleNumber || draft.vehicleNumber || '',
            lsp: session.lsp || draft.lsp || '',
            dockNumber: session.dockNumber || draft.dockNumber || '',
            scannerName: session.scannerName || draft.scannerName || '',
            invalid: {},
            formError: false,
            starting: false,
            // Set once field-level validation passes, to show the "Confirm Trip
            // Details" acknowledgement (see startLoading/confirmStartLoading) -
            // never persisted across a trip switch since startLoading always
            // resets it fresh on every attempt.
            pendingStartConfirm: false,
            allPlanRowsForDate: [],
            loadingPlans: true,
            tripStatuses: [],
            // ONLY a live session locks here - session.loadVehicle is only
            // ever set via startSession, which startLoading only calls once
            // EVERY manual field has already passed its own non-empty
            // validation, so session.vehicleNumber being present too is
            // guaranteed, not just likely. That guarantee is exactly what
            // locking is protecting: this trip's identity was already used to
            // start a real trip_status/scan_log record, so retyping it here
            // would silently mismatch what the backend already has.
            // The sessionStorage draft never gets the same treatment,
            // deliberately - a draft is unsubmitted, still-being-typed
            // scratch data (that's its whole purpose - surviving an
            // accidental refresh mid-typing, see the file's own top comment),
            // not a record of anything actually used yet. Locking based on
            // the draft (this used to check draft.loadVehicle, then
            // draft.loadVehicle+draft.vehicleNumber) kept incorrectly
            // treating "the operator got partway through typing this trip's
            // form" as "this trip's identity is already committed" -
            // whichever subset of fields happened to be filled in before the
            // refresh, the REST would still get wrongly locked as empty.
            // Re-picking the same Trip No from the dropdown after a refresh
            // still runs handleTripNoChange's own real backend lookup, which
            // locks correctly if that trip genuinely turns out to already be
            // in progress.
            fieldsLocked: !!(session.loadVehicle && session.vehicleNumber),
            loadingResumeDetails: false,
            // Set (to a {vehicleNumber, existingTripNo} object) when the
            // Vehicle Number just entered already belongs to a DIFFERENT trip
            // scheduled for the same Load Date - see handleVehicleNumberBlur.
            vehicleNoDuplicateWarning: null,
            // The exact vehicleNumber value the operator already said "yes,
            // keep it" for - skips re-showing the same warning every time the
            // field loses focus again without the value actually changing.
            vehicleNoAcknowledged: ''
        };
    }

    componentDidMount() {
        this._lastPortCode = this.context.portCode;
        // The Dashboard's Resume button lands here (not straight on Scan anymore -
        // see MobileDashboard.resumeFromDashboard) carrying just the three ids
        // needed to re-derive everything else below, the same way picking this
        // trip manually would - applyResumeFrom takes it from there instead of
        // the plain loadPlansForDate this mount would otherwise do.
        var resumeFrom = this.props.location && this.props.location.state && this.props.location.state.resumeFrom;
        if (resumeFrom) {
            this.applyResumeFrom(resumeFrom);
            return;
        }
        this.loadPlansForDate();
        if (this.state.planId) {
            this.loadTripStatuses(this.state.planId).then(() => {
                // A restored Trip No (from a live session OR the sessionStorage
                // draft) was never actually verified against real backend data -
                // the constructor's own fieldsLocked guess only trusts a LIVE
                // session (session.loadVehicle && session.vehicleNumber, which
                // startLoading guarantees), so it's already correct and locked
                // in that case; skipped here to avoid redundantly re-fetching
                // for something already right. Everything else (draft-restored,
                // or a live session that for whatever reason came back without
                // a vehicleNumber) still needs the same real lookup a fresh
                // dropdown pick already gets via handleTripNoChange - a refresh
                // can't tell "this trip was genuinely started" apart from "the
                // operator was still midway through typing" any other way.
                if (this.state.loadVehicle && !this.state.fieldsLocked) {
                    this.reconcileRestoredTripNo();
                }
            });
        }
    }

    // Re-validates a restored (not-yet-confirmed) Trip No against the real
    // trip_statuses just fetched. Deliberately asymmetric with
    // handleTripNoChange, which this mirrors for the "in progress" case only -
    // it ONLY ever locks (when the trip turns out to genuinely be started for
    // real); it never clears/unlocks anything when the trip ISN'T in progress,
    // unlike a fresh dropdown pick would. Clearing here would wipe out exactly
    // what the sessionStorage draft exists to preserve across a refresh - an
    // operator who was simply midway through typing (Start Loading never
    // tapped) must get their draft back untouched, not reset to blank just
    // because this trip doesn't (yet) have a real backend record.
    reconcileRestoredTripNo = () => {
        var tripNo = this.state.loadVehicle;
        var planId = this.state.planId;
        if (!this.isTripInProgress(tripNo)) return;
        var statusRow = this.state.tripStatuses.find(t => t.trip_no === tripNo);
        if (this.hasResumableDetails(statusRow)) {
            this.setState({
                vehicleNumber: statusRow.vehicle_number || '', lsp: statusRow.lsp || '',
                dockNumber: statusRow.dock_number || '', scannerName: statusRow.scanner_name || '',
                fieldsLocked: true, loadingResumeDetails: false
            });
            return;
        }
        // In progress but trip_status has nothing resumable recorded (a trip
        // started before these fields existed there) - same scan_log fallback
        // handleTripNoChange's own fresh-pick path already uses.
        this.setState({ loadingResumeDetails: true });
        this.context.fetchScanLogReal(planId, tripNo).then((scanRows) => {
            // The operator already moved on to a different plan/trip while
            // this was in flight - that selection now owns the fields.
            if (this.state.planId !== planId || this.state.loadVehicle !== tripNo) return;
            var withDetails = (scanRows || []).find(r => r.vehicle_number || r.lsp || r.dock_number || r.scanner_name);
            this.setState({
                loadingResumeDetails: false,
                vehicleNumber: (withDetails && withDetails.vehicle_number) || '',
                lsp: (withDetails && withDetails.lsp) || '',
                dockNumber: (withDetails && withDetails.dock_number) || '',
                scannerName: (withDetails && withDetails.scanner_name) || '',
                fieldsLocked: !!withDetails
            });
        }).catch((e) => {
            console.log(e);
            if (this.state.planId !== planId || this.state.loadVehicle !== tripNo) return;
            this.setState({ loadingResumeDetails: false });
        });
    };

    // Drives the whole form purely from {loadDate, planId, tripNo} - loads that
    // date's plan rows to resolve loadType, then trip statuses to resolve
    // resumable details, then reuses handleTripNoChange for the actual
    // locking/prefill logic so this ends up in exactly the same state as if the
    // operator had picked this trip by hand. tripStatuses is set into state
    // (awaited) before handleTripNoChange runs - it reads this.state.tripStatuses
    // synchronously, so calling it before the fetch resolves would wrongly see
    // no trip statuses yet and treat this as a fresh (non-resumable) pick.
    applyResumeFrom = (resumeFrom) => {
        this.setState({
            loadDate: resumeFrom.loadDate, planId: '', loadType: '', loadVehicle: '',
            tripStatuses: [], fieldsLocked: false, loadingResumeDetails: true, loadingPlans: true
        });
        this.context.fetchPlanRowsForDate(resumeFrom.loadDate).then((rows) => {
            this.setState({ allPlanRowsForDate: rows, loadingPlans: false });
            var row = rows.find(r => r.transaction_id === resumeFrom.planId && r.trip_no === resumeFrom.tripNo);
            if (!row) {
                // The plan row this trip needs is gone (e.g. deleted since) - nothing
                // to resume into, leave the form open for a manual pick instead of
                // getting stuck on a permanent "checking details" state.
                this.setState({ loadingResumeDetails: false });
                return;
            }
            this.setState({ planId: resumeFrom.planId, loadType: row.trip_type });
            return this.context.fetchTripStatuses(resumeFrom.planId).then((tripStatuses) => {
                this.setState({ tripStatuses }, () => {
                    this.handleTripNoChange({ target: { value: resumeFrom.tripNo } });
                });
            });
        }).catch((e) => {
            console.log(e);
            this.setState({ loadingPlans: false, loadingResumeDetails: false });
        });
    };

    componentDidUpdate(prevProps, prevState) {
        if (this.context.portCode !== this._lastPortCode) {
            this._lastPortCode = this.context.portCode;
            this.setState({ planId: '', loadType: '', loadVehicle: '', tripStatuses: [] });
            this.loadPlansForDate();
            return;
        }
        if (DRAFT_FIELDS.some((field) => prevState[field] !== this.state[field])) {
            var draftFields = {};
            DRAFT_FIELDS.forEach((field) => { draftFields[field] = this.state[field]; });
            saveInputPlanDraft(draftFields);
        }
    }

    loadPlansForDate = () => {
        this.setState({ loadingPlans: true });
        this.context.fetchPlanRowsForDate(this.state.loadDate).then((rows) => {
            this.setState({ allPlanRowsForDate: rows, loadingPlans: false });
        }).catch((e) => {
            console.log(e);
            this.setState({ allPlanRowsForDate: [], loadingPlans: false });
        });
    };

    // Same +2-day grace window the desktop's "Upload Loading Plan" button uses
    // (viewLoadSummaryDetails.js's isUploadWindowOpen), applied the other
    // direction here: Load Date used to be fixed to today with no way to
    // change it - now the operator can look back up to 2 days (today's the
    // upper bound; going forward would mean planning for a date that hasn't
    // been uploaded yet, which isn't the point of this screen). Local
    // getters/padding, not toISOString() - a local midnight Date's own
    // toISOString() renders as the PREVIOUS day in any positive-UTC-offset
    // timezone (e.g. IST), which would silently shift this whole window by a
    // day.
    loadDateBounds = () => {
        var pad = (n) => String(n).padStart(2, '0');
        var fmt = (d) => d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate());
        var today = new Date();
        var min = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 2);
        return { min: fmt(min), max: fmt(today) };
    };

    handleLoadDateChange = (e) => {
        var value = e.target.value;
        if (!value) return;
        var bounds = this.loadDateBounds();
        // Belt-and-suspenders alongside the input's own min/max - a native
        // date input already blocks picking outside that range in every
        // browser this app targets, but a value can still arrive out of
        // range from autofill/paste.
        if (value < bounds.min || value > bounds.max) return;
        // Changing the date re-scopes the whole form (different plan_date's
        // plan rows), same as picking a new Plan Id does in
        // handlePlanIdChange - everything downstream has to reset, not just
        // silently keep a planId/tripNo that may not even exist for the new date.
        this.setState({ loadDate: value, planId: '', loadType: '', loadVehicle: '', tripStatuses: [], fieldsLocked: false, loadingResumeDetails: false, vehicleNoDuplicateWarning: null, vehicleNoAcknowledged: '' }, () => {
            this.loadPlansForDate();
        });
    };

    // Returns the promise (not fire-and-forget) - componentDidMount chains
    // onto it to reconcile a restored Trip No against real data once it's in.
    loadTripStatuses = (transactionId) => {
        return this.context.fetchTripStatuses(transactionId).then((tripStatuses) => {
            this.setState({ tripStatuses });
        }).catch((e) => {
            console.log(e);
            this.setState({ tripStatuses: [] });
        });
    };

    setField = (field, value) => {
        this.setState((prev) => ({
            [field]: value,
            invalid: { ...prev.invalid, [field]: false }
        }));
    };

    handlePlanIdChange = (e) => {
        var planId = e.target.value;
        var ctx = this.context;
        var nextState = { planId, loadType: '', loadVehicle: '', tripStatuses: [], fieldsLocked: false, loadingResumeDetails: false, vehicleNoDuplicateWarning: null, vehicleNoAcknowledged: '' };
        this.setState((prev) => ({ ...nextState, invalid: { ...prev.invalid, planId: false } }));
        if (planId) {
            this.loadTripStatuses(planId);
            // Restore the previously selected Load Type/Trip No for this same plan
            // (e.g. coming back from Scan mid-session).
            if (planId === ctx.session.planId && ctx.session.loadType) {
                // Same reasoning as the constructor - session's own manual-input
                // values are already validated/authoritative for this trip, so lock
                // immediately rather than leaving fieldsLocked at the reset-to-false
                // above (which would otherwise show the "not found" warning wrongly).
                this.setState({
                    loadType: ctx.session.loadType, loadVehicle: ctx.session.loadVehicle || '',
                    vehicleNumber: ctx.session.vehicleNumber || '', lsp: ctx.session.lsp || '',
                    dockNumber: ctx.session.dockNumber || '', scannerName: ctx.session.scannerName || '',
                    fieldsLocked: !!ctx.session.loadVehicle
                });
            }
        }
    };

    handleLoadTypeChange = (e) => {
        this.setField('loadType', e.target.value);
        this.setState({ loadVehicle: '', vehicleNumber: '', lsp: '', dockNumber: '', scannerName: '', fieldsLocked: false, loadingResumeDetails: false });
    };

    // Picking a trip that's already in progress prefills its real, already-started
    // identity and locks the fields - typing something different here would
    // silently mismatch what's already on this trip's scan_log rows. trip_status
    // is tried first (no extra fetch, and covers a trip with zero scans so far,
    // where scan_log would have nothing); if it has no details recorded (trips
    // started before that field was added there), fall back to scan_log itself -
    // every scan has always stamped these fields from the session, so any trip
    // that's actually been scanned at least once still has a reliable source.
    // Only a trip that was started AND never scanned even once under the old code
    // has genuinely nothing anywhere, and only then are the fields left open -
    // safe, since there's no prior scan to vary from yet.
    // Picking a fresh trip clears back to blank so a locked resume value can never
    // leak into what should be a brand new manual entry.
    handleTripNoChange = (e) => {
        var tripNo = e.target.value;
        var planId = this.state.planId;
        this.setField('loadVehicle', tripNo);
        // Which trip is "the current one" (excluded from
        // findDuplicateVehicleTrip) just changed - an acknowledgement/warning
        // from the previous trip_no selection doesn't necessarily still apply.
        this.setState({ vehicleNoDuplicateWarning: null, vehicleNoAcknowledged: '' });

        if (!tripNo || !this.isTripInProgress(tripNo)) {
            this.setState({ vehicleNumber: '', lsp: '', dockNumber: '', scannerName: '', fieldsLocked: false, loadingResumeDetails: false });
            return;
        }

        var statusRow = this.state.tripStatuses.find(t => t.trip_no === tripNo);
        if (this.hasResumableDetails(statusRow)) {
            this.setState({
                vehicleNumber: statusRow.vehicle_number || '', lsp: statusRow.lsp || '',
                dockNumber: statusRow.dock_number || '', scannerName: statusRow.scanner_name || '',
                fieldsLocked: true, loadingResumeDetails: false
            });
            return;
        }

        this.setState({ vehicleNumber: '', lsp: '', dockNumber: '', scannerName: '', fieldsLocked: false, loadingResumeDetails: true });
        this.context.fetchScanLogReal(planId, tripNo).then((scanRows) => {
            // The user already moved on to a different plan/trip while this was
            // in flight - that selection's own handleTripNoChange call owns the
            // fields now, applying this stale result would clobber it.
            if (this.state.planId !== planId || this.state.loadVehicle !== tripNo) return;
            var withDetails = (scanRows || []).find(r => r.vehicle_number || r.lsp || r.dock_number || r.scanner_name);
            this.setState({
                loadingResumeDetails: false,
                vehicleNumber: (withDetails && withDetails.vehicle_number) || '',
                lsp: (withDetails && withDetails.lsp) || '',
                dockNumber: (withDetails && withDetails.dock_number) || '',
                scannerName: (withDetails && withDetails.scanner_name) || '',
                fieldsLocked: !!withDetails
            });
        }).catch((e) => {
            console.log(e);
            if (this.state.planId !== planId || this.state.loadVehicle !== tripNo) return;
            this.setState({ loadingResumeDetails: false, fieldsLocked: false });
        });
    };

    isTripLocked = (tripNo) => {
        return this.state.tripStatuses.some(t => t.trip_no === tripNo && t.loading_started);
    };

    // Completed implies started too, so this has to be checked ahead of/alongside
    // isTripLocked wherever the two need different wording - only a completed trip
    // is actually done and truly can't be picked again.
    isTripCompleted = (tripNo) => {
        return this.state.tripStatuses.some(t => t.trip_no === tripNo && t.loading_completed);
    };

    // Started but never finished - e.g. the app was closed mid-scan, so nothing
    // ever flipped loading_completed. This trip has to stay pickable (as a resume,
    // not a fresh start) or the operator is locked out of it forever.
    isTripInProgress = (tripNo) => {
        return this.isTripLocked(tripNo) && !this.isTripCompleted(tripNo);
    };

    // A trip started before these fields were captured (or started some other
    // way that never recorded them) has nothing real to lock in - disabling blank
    // inputs would trap the operator with no way to ever fill them in. Only lock
    // when there's an actual original value to protect against being overwritten.
    hasResumableDetails = (row) => {
        return !!(row && (row.vehicle_number || row.lsp || row.dock_number || row.scanner_name));
    };

    getPlanRows = () => {
        return this.state.allPlanRowsForDate.filter(r => r.transaction_id === this.state.planId);
    };

    // A real vehicle can only physically be on one trip at a time, so the same
    // Vehicle Number showing up on a DIFFERENT trip that hasn't finished
    // loading yet is almost certainly a typo (or the wrong trip picked), not
    // a genuine reuse. A trip whose loading is already completed is not a
    // conflict at all - the vehicle is free again and can be picked for a new
    // trip same-day or any day, so it's excluded here rather than restricted
    // by date (a same-day-only check can't tell "still on the road" apart
    // from "done hours ago"). tripStatuses is already loaded for the whole
    // Plan Id (every trip_no, not just the one selected here) - no extra
    // fetch needed, same data handleTripNoChange already reads. Excludes the
    // CURRENTLY selected trip_no itself (resuming a trip trivially "matches"
    // its own already-recorded vehicle number) and a case-insensitive,
    // trimmed comparison (operators aren't always consistent about casing
    // when retyping a plate number).
    findDuplicateVehicleTrip = (vehicleNumber) => {
        var trimmed = (vehicleNumber || '').trim().toLowerCase();
        if (!trimmed) return null;
        return this.state.tripStatuses.find((t) =>
            String(t.trip_no) !== String(this.state.loadVehicle) &&
            (t.vehicle_number || '').trim().toLowerCase() === trimmed &&
            !t.loading_completed
        ) || null;
    };

    // Checked on blur, not on every keystroke - this is a "please confirm you
    // meant this" prompt, not a hard validation error, so it shouldn't
    // interrupt the operator mid-typing. Skips re-prompting for the exact
    // value already acknowledged (vehicleNoAcknowledged), so tabbing in and
    // back out of the field without changing it doesn't nag a second time.
    handleVehicleNumberBlur = () => {
        var trimmed = (this.state.vehicleNumber || '').trim();
        if (!trimmed || trimmed === this.state.vehicleNoAcknowledged) return;
        var match = this.findDuplicateVehicleTrip(trimmed);
        if (match) {
            this.setState({ vehicleNoDuplicateWarning: { vehicleNumber: trimmed, existingTripNo: match.trip_no } });
        }
    };

    // "Yes, keep it" just remembers this exact value as acknowledged, so
    // blurring the field again without changing it doesn't re-prompt.
    // "No, remove it" clears the field outright - the operator types a
    // different vehicle number in, same as if they'd never entered this one.
    resolveVehicleNoDuplicateWarning = (keep) => {
        if (keep) {
            this.setState({ vehicleNoAcknowledged: this.state.vehicleNoDuplicateWarning.vehicleNumber, vehicleNoDuplicateWarning: null });
        } else {
            this.setField('vehicleNumber', '');
            this.setState({ vehicleNoAcknowledged: '', vehicleNoDuplicateWarning: null });
        }
    };

    // Vehicle Number: letters and digits only - no spaces, no separators. Real
    // registration numbers (e.g. HR55AC2311) don't carry punctuation, so
    // anything else typed in is almost certainly a typo, not a legitimate format.
    isValidVehicleNumber = (value) => /^[A-Za-z0-9]+$/.test(value);

    // LSP: a transporter name, so spaces are allowed (letters/digits/spaces
    // only) - but has to be more than 3 characters, since a 1-3 char value is
    // almost always a stray initial or accidental keystroke rather than a real
    // transporter name.
    isValidLSP = (value) => /^[A-Za-z0-9 ]+$/.test(value) && value.length > 3;

    startLoading = () => {
        var { loadDate, planId, loadType, loadVehicle, vehicleNumber, lsp, dockNumber, scannerName } = this.state;
        var vehicleTrimmed = vehicleNumber.trim();
        var lspTrimmed = lsp.trim();
        var vehicleNumberInvalid = !vehicleTrimmed || !this.isValidVehicleNumber(vehicleTrimmed);
        var lspInvalid = !lspTrimmed || !this.isValidLSP(lspTrimmed);
        var invalid = {
            loadDate: !loadDate, planId: !planId, loadType: !loadType, loadVehicle: !loadVehicle,
            vehicleNumber: vehicleNumberInvalid, lsp: lspInvalid, dockNumber: !dockNumber.trim(), scannerName: !scannerName.trim()
        };
        this.setState({ invalid });
        if (Object.values(invalid).some(Boolean)) {
            // Most specific message wins - a format problem on a field that's
            // already non-empty is more useful to the operator than the generic
            // "fill in all fields" catch-all.
            var message = 'Please fill in all fields before starting.';
            if (vehicleTrimmed && vehicleNumberInvalid) {
                message = 'Vehicle Number must contain only letters and numbers, with no spaces or special characters.';
            } else if (lspTrimmed && !this.isValidLSP(lspTrimmed)) {
                message = lspTrimmed.length <= 3
                    ? 'LSP must be more than 3 characters.'
                    : 'LSP must contain only letters, numbers, and spaces.';
            }
            this.setState({ formError: message });
            return;
        }
        // Field-level validation passed - show the "confirm before you commit"
        // acknowledgement (see confirmStartLoading) instead of starting
        // immediately. Tapping Edit there just closes this without touching any
        // field, so the operator lands back on a fully-editable form; tapping OK
        // runs the exact same start/resume logic this used to run right here.
        this.setState({ formError: false, pendingStartConfirm: true });
    };

    cancelStartConfirm = () => {
        this.setState({ pendingStartConfirm: false });
    };

    confirmStartLoading = () => {
        var ctx = this.context;
        var { loadDate, planId, loadType, loadVehicle, vehicleNumber, lsp, dockNumber, scannerName } = this.state;
        this.setState({ pendingStartConfirm: false, starting: true });

        // Only a completed trip is actually blocked - a started-but-not-finished one
        // (e.g. the app was closed mid-scan, so loading_completed never got set) is
        // resumed below instead of being permanently stranded.
        if (this.isTripCompleted(loadVehicle)) {
            this.setState({ starting: false });
            alert(`Plan ${planId} Trip ${loadVehicle} has already been fully loaded. Please pick a different trip.`);
            return;
        }
        // Every HU planned under this plan_id, across ALL trip nos - not just this
        // trip's own subset (hus, below) - so Scan can tell "wrong truck" (HU exists,
        // but under a different trip no in this same plan) apart from "not found"
        // (HU isn't in this plan at all). See MobileTruckLoadContext.processScanOutcome.
        var allPlanHUs = this.getPlanRows()
            .map(r => ({ id: r.hu, sub: r.doc_no, consignee: r.consignee, docType: r.doc_type, tripNo: r.trip_no }));
        var hus = allPlanHUs.filter(r => r.tripNo === loadVehicle);
        if (hus.length === 0) {
            this.setState({ starting: false });
            alert(`Plan ${planId} (Trip ${loadVehicle}) has no records. Please check the plan and try again.`);
            return;
        }

        var resuming = this.isTripInProgress(loadVehicle);
        var sessionFields = {
            loadDate, planId, loadType, loadVehicle,
            vehicleNumber: vehicleNumber.trim(), lsp: lsp.trim(),
            dockNumber: dockNumber.trim(), scannerName: scannerName.trim(),
            expectedHUs: hus, allPlanHUs: allPlanHUs
        };

        // startTripLoadingReal is an upsert ($setOnInsert) - safe to call again for
        // an already-started trip (details are only ever written the first time),
        // so no need to branch on `resuming` here too.
        var details = {
            vehicle_number: sessionFields.vehicleNumber, lsp: sessionFields.lsp,
            dock_number: sessionFields.dockNumber, scanner_name: sessionFields.scannerName,
            load_type: sessionFields.loadType
        };
        ctx.startTripLoadingReal(planId, loadVehicle, details).then(() => {
            if (!resuming) {
                ctx.startSession(sessionFields);
                this.setState({ starting: false });
                // planId/loadVehicle in the path (not just the live session) so a
                // real refresh on Scan can rebuild itself via resumeSessionFromIds
                // instead of losing everything - see that method's own comment.
                this.props.history.push(`/lm-mobile/scan/${planId}/${loadVehicle}`);
                return;
            }
            // Rebuild progress from the trip's real scan log instead of starting over
            // with every HU marked remaining - every scan so far was already
            // persisted via recordScanReal as it happened.
            return ctx.fetchScanLogReal(planId, loadVehicle).then((scanRows) => {
                ctx.resumeSession(sessionFields, scanRows);
                this.setState({ starting: false });
                this.props.history.push(`/lm-mobile/scan/${planId}/${loadVehicle}`);
            });
        }).catch((e) => {
            console.log(e);
            this.setState({ starting: false });
            alert('Could not start loading - please try again.');
        });
    };

    render() {
        var { loadDate, planId, loadType, loadVehicle, vehicleNumber, lsp, dockNumber, scannerName, invalid, formError, starting, allPlanRowsForDate, fieldsLocked, loadingResumeDetails, pendingStartConfirm } = this.state;
        var loadDateBounds = this.loadDateBounds();

        var planIdOptions = [...new Set(allPlanRowsForDate.map(r => r.transaction_id))];
        var planRows = this.getPlanRows();
        var uploaded = planId && planRows.length > 0;
        var loadTypeOptions = uploaded ? [...new Set(planRows.map(r => r.trip_type))] : [];
        var tripNoOptions = (uploaded && loadType)
            // Ascending by leading number, not insertion order - trip_no is
            // usually a plain numeric string ("31", "4", "12") but can also be
            // a combined label for a merged trip ("6+7"), which parseInt still
            // reads correctly as 6 for sort purposes even though it's not a
            // clean number on its own.
            ? [...new Set(planRows.filter(r => r.trip_type === loadType).map(r => r.trip_no))]
                .sort((a, b) => parseInt(a, 10) - parseInt(b, 10))
            : [];
        // fieldsLocked/loadingResumeDetails are tracked in state, not derived here -
        // locking can depend on an async scan_log lookup (see handleTripNoChange),
        // not just the synchronously-available tripStatuses.
        var startReady = uploaded && loadType && loadVehicle && !loadingResumeDetails;
        // isResuming drives the button label and (in startLoading) which session
        // path runs - true for ANY in-progress trip, regardless of whether real
        // details were actually found for it.
        var isResuming = !!(loadVehicle && this.isTripInProgress(loadVehicle));

        return (
            <>
                <div className="mtl-screen-scroll">
                        <div className="mtl-breadcrumb"><span className="mtl-crumb-parent">Truck Load Automation</span><span className="mtl-crumb-sep">&rsaquo;</span><b>Input Plan</b></div>
                        <div className="mtl-form">
                            <div className="mtl-filter-panel">
                                <div className="mtl-form-grid">
                                    <div className="mtl-form-group">
                                        <label>Load Date</label>
                                        <input
                                            className="mtl-field"
                                            type="date"
                                            value={loadDate}
                                            min={loadDateBounds.min}
                                            max={loadDateBounds.max}
                                            onChange={this.handleLoadDateChange}
                                            style={{ marginBottom: 0 }}
                                        />
                                    </div>
                                    <div className="mtl-form-group">
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            Plan Id
                                            {/* Disabling the select alone wasn't a strong enough signal on
                                                a slow connection - looked identical to broken/empty. */}
                                            {this.state.loadingPlans && <span className="mtl-spinner" style={{ width: '10px', height: '10px' }}></span>}
                                        </label>
                                        <select className={"mtl-field" + (invalid.planId ? ' mtl-invalid' : '')} value={planId} onChange={this.handlePlanIdChange} disabled={this.state.loadingPlans} style={{ marginBottom: 0 }}>
                                            <option value="">{this.state.loadingPlans ? 'Loading plans…' : '-- Select Plan Id --'}</option>
                                            {planIdOptions.map(id => <option key={id} value={id}>{id}</option>)}
                                        </select>
                                    </div>
                                    <div className="mtl-form-group">
                                        <label>Trip Type</label>
                                        <select className={"mtl-field" + (invalid.loadType ? ' mtl-invalid' : '')} value={loadType} disabled={!uploaded} onChange={this.handleLoadTypeChange} style={{ marginBottom: 0 }}>
                                            <option value="">-- Select Trip Type --</option>
                                            {loadTypeOptions.map(v => <option key={v} value={v}>{v}</option>)}
                                        </select>
                                    </div>
                                    <div className="mtl-form-group">
                                        <label>Trip No</label>
                                        <select className={"mtl-field" + (invalid.loadVehicle ? ' mtl-invalid' : '')} value={loadVehicle} disabled={!loadType} onChange={this.handleTripNoChange} style={{ marginBottom: 0 }}>
                                            <option value="">-- Select Trip No --</option>
                                            {tripNoOptions.map(v => {
                                                // Only a completed trip is truly locked out - a started-but-not-finished
                                                // one stays pickable so it can be resumed instead of stranded forever.
                                                var completed = this.isTripCompleted(v);
                                                var inProgress = this.isTripInProgress(v);
                                                var suffix = completed ? ' - Loading Completed' : (inProgress ? ' - In Progress (tap to resume)' : '');
                                                return <option key={v} value={v} disabled={completed}>{v}{suffix}</option>;
                                            })}
                                        </select>
                                    </div>
                                </div>
                            </div>
                            {!this.state.loadingPlans && planIdOptions.length === 0 && (
                                <div className="mtl-error" style={{ display: 'block' }}>No plan data has been uploaded for this warehouse on {loadDate} yet.</div>
                            )}

                            <div className="mtl-manual-group">
                                <div className="mtl-manual-group-head">
                                    <span className="mtl-manual-group-icon">
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="4" width="16" height="16" rx="2"></rect><line x1="9" y1="9" x2="15" y2="9"></line><line x1="9" y1="13" x2="15" y2="13"></line></svg>
                                    </span>
                                    <span className="mtl-manual-group-title">Manual Input</span>
                                </div>
                                {isResuming && (
                                    <div className="mtl-error" style={{ display: 'block', background: '#fff3e0', color: '#b26a00' }}>
                                        {loadingResumeDetails
                                            ? 'Checking this trip\'s recorded details...'
                                            : fieldsLocked
                                                ? 'This trip is already in progress - showing its original Vehicle Number/LSP, locked, to avoid a mismatch. Dock Number/Scanner Name are pre-filled but can be changed if either has switched since loading started.'
                                                : 'This trip is already in progress, but no original details were found for it anywhere - please enter them carefully to avoid creating inconsistent records for this trip.'}
                                    </div>
                                )}
                                <div className="mtl-form-grid">
                                    <div className="mtl-form-group">
                                        <label>Vehicle Number</label>
                                        <input className={"mtl-field" + (invalid.vehicleNumber ? ' mtl-invalid' : '')} type="text" placeholder="e.g. HR55AC2311" value={vehicleNumber} disabled={fieldsLocked || loadingResumeDetails} onChange={(e) => this.setField('vehicleNumber', e.target.value)} onBlur={this.handleVehicleNumberBlur} />
                                    </div>
                                    <div className="mtl-form-group">
                                        <label>LSP</label>
                                        <input className={"mtl-field" + (invalid.lsp ? ' mtl-invalid' : '')} type="text" placeholder="e.g. NSL" value={lsp} disabled={fieldsLocked || loadingResumeDetails} onChange={(e) => this.setField('lsp', e.target.value)} />
                                    </div>
                                    <div className="mtl-form-group">
                                        <label>Dock Number</label>
                                        {/* Not gated by fieldsLocked, unlike Vehicle Number/LSP above - the
                                            dock in use can genuinely change mid-loading (a different bay takes
                                            over), so this stays editable on a resume; only loadingResumeDetails
                                            (the async lookup still in flight) blocks it, to avoid a race. */}
                                        <input className={"mtl-field" + (invalid.dockNumber ? ' mtl-invalid' : '')} type="text" placeholder="e.g. DOCK-04" value={dockNumber} disabled={loadingResumeDetails} onChange={(e) => this.setField('dockNumber', e.target.value)} />
                                    </div>
                                    <div className="mtl-form-group">
                                        <label>Scanner Name</label>
                                        <input className={"mtl-field" + (invalid.scannerName ? ' mtl-invalid' : '')} type="text" placeholder="e.g. Scanner01" value={scannerName} disabled={loadingResumeDetails} onChange={(e) => this.setField('scannerName', e.target.value)} />
                                    </div>
                                </div>
                            </div>
                            {formError && <div className="mtl-error" style={{ display: 'block' }}>{formError}</div>}
                        </div>
                    </div>
                <div className="mtl-actions1">
                    <button className={"mtl-start" + (starting ? ' mtl-loading' : '')} disabled={!startReady || starting} onClick={this.startLoading}>
                        <span className="mtl-btn-label">{isResuming ? 'Resume Loading' : 'Start Loading'}</span>
                        <span className="mtl-btn-spinner"></span>
                    </button>
                </div>
                {/* A soft confirmation, not a hard block (unlike the
                    isTripCompleted check in startLoading) - the operator
                    might genuinely know this pending trip is stale/abandoned,
                    so they get to decide instead of being refused outright. */}
                {this.state.vehicleNoDuplicateWarning && (
                    <div style={{ position: 'fixed', inset: 0, zIndex: 3000, background: 'rgba(15,23,42,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
                        <div style={{ width: '100%', maxWidth: '340px', background: '#fff', borderRadius: '16px', padding: '22px 20px', textAlign: 'center', boxShadow: '0 12px 32px rgba(0,0,0,0.35)' }}>
                            <div style={{ fontSize: '32px', lineHeight: 1, marginBottom: '10px', color: '#e65100' }}>⚠</div>
                            <div style={{ fontWeight: 800, fontSize: '15px', color: '#1a1a1a', marginBottom: '6px' }}>Vehicle On a Pending Trip</div>
                            <div style={{ fontSize: '13px', color: '#555', marginBottom: '18px' }}>
                                Vehicle "{this.state.vehicleNoDuplicateWarning.vehicleNumber}" is already assigned to Trip {this.state.vehicleNoDuplicateWarning.existingTripNo}, which hasn't finished loading yet. A vehicle can only be on one trip at a time - are you sure you want to use it again for this trip?
                            </div>
                            <div style={{ display: 'flex', gap: '10px' }}>
                                <button
                                    type="button"
                                    onClick={() => this.resolveVehicleNoDuplicateWarning(false)}
                                    style={{ flex: 1, padding: '11px', borderRadius: '10px', border: '1px solid #cfd8dc', background: '#fff', color: '#37474f', fontWeight: 700, fontSize: '13px', cursor: 'pointer' }}
                                >No, remove it</button>
                                <button
                                    type="button"
                                    onClick={() => this.resolveVehicleNoDuplicateWarning(true)}
                                    style={{ flex: 1, padding: '11px', borderRadius: '10px', border: 'none', background: '#1976d2', color: '#fff', fontWeight: 800, fontSize: '13px', cursor: 'pointer' }}
                                >Yes, keep it</button>
                            </div>
                        </div>
                    </div>
                )}
                {/* One last look before this trip actually locks in Vehicle
                    Number/LSP and starts writing to the backend. Edit just
                    closes this - the form underneath was never disabled, so
                    the operator lands right back on it able to change
                    anything; OK runs confirmStartLoading, the exact same
                    start/resume logic startLoading used to run immediately. */}
                {pendingStartConfirm && (
                    <div style={{ position: 'fixed', inset: 0, zIndex: 3000, background: 'rgba(15,23,42,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
                        <div style={{ width: '100%', maxWidth: '340px', background: '#fff', borderRadius: '16px', padding: '22px 20px', textAlign: 'center', boxShadow: '0 12px 32px rgba(0,0,0,0.35)' }}>
                            <div style={{ fontSize: '32px', lineHeight: 1, marginBottom: '10px', color: '#1976d2' }}>&#10003;</div>
                            <div style={{ fontWeight: 800, fontSize: '15px', color: '#1a1a1a', marginBottom: '10px' }}>Confirm Trip Details</div>
                            <div style={{ textAlign: 'left', fontSize: '13px', color: '#37474f', background: '#f8fafc', border: '1px solid #e0e6ed', borderRadius: '10px', padding: '12px 14px', marginBottom: '16px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}><span style={{ color: '#78909c' }}>Trip No</span><b>{loadVehicle}</b></div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}><span style={{ color: '#78909c' }}>Vehicle Number</span><b>{vehicleNumber.trim()}</b></div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}><span style={{ color: '#78909c' }}>LSP</span><b>{lsp.trim()}</b></div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}><span style={{ color: '#78909c' }}>Dock Number</span><b>{dockNumber.trim()}</b></div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: '#78909c' }}>Scanner Name</span><b>{scannerName.trim()}</b></div>
                            </div>
                            <div style={{ fontSize: '12px', color: '#78909c', marginBottom: '16px' }}>Are these details correct? Vehicle Number and LSP can't be changed once loading starts.</div>
                            <div style={{ display: 'flex', gap: '10px' }}>
                                <button
                                    type="button"
                                    onClick={this.cancelStartConfirm}
                                    style={{ flex: 1, padding: '11px', borderRadius: '10px', border: '1px solid #cfd8dc', background: '#fff', color: '#37474f', fontWeight: 700, fontSize: '13px', cursor: 'pointer' }}
                                >Edit</button>
                                <button
                                    type="button"
                                    onClick={this.confirmStartLoading}
                                    style={{ flex: 1, padding: '11px', borderRadius: '10px', border: 'none', background: '#1976d2', color: '#fff', fontWeight: 800, fontSize: '13px', cursor: 'pointer' }}
                                >OK, Proceed</button>
                            </div>
                        </div>
                    </div>
                )}
            </>
        );
    }
}
